import type { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs';
import type { DocumentNode, WatchQueryOptions } from '@apollo/client';
import type {
    MeasuringPointCommentQuery,
    MeasuringPointCommentQueryVariables,
    MeasuringPointCommentsQuery,
    MeasuringPointCommentsQueryVariables,
    MeasuringPointCommentType,
} from '@/graphql/types';
import type { OperationVariables } from '@apollo/client/core/types';
import { fromObservableQuery } from '../../../utils/rxjs';
import { getClient } from '../../../utils/apollo-client';
import { format } from '../../../utils/formatting';

import { MEASURING_POINT_COMMENT_QUERY, MEASURING_POINT_COMMENTS_QUERY } from './queries';

function fetch$<TData, TVariables extends OperationVariables>(
    query: DocumentNode,
    variables: TVariables,
    isLoading$: BehaviorSubject<boolean>,
    error$: BehaviorSubject<Error | undefined>
) {
    const queryParameters: WatchQueryOptions<TVariables, TData> = {
        variables,
        query,
        fetchPolicy: 'cache-and-network',
    };

    return fromObservableQuery(getClient().watchQuery(queryParameters), {
        isLoading$,
        error$,
        useInitialLoading: true,
    });
}

export function fetchComment$(
    id: string,
    isLoading$: BehaviorSubject<boolean>,
    error$: BehaviorSubject<Error | undefined>
) {
    return fetch$<MeasuringPointCommentQuery, MeasuringPointCommentQueryVariables>(
        MEASURING_POINT_COMMENT_QUERY,
        { id },
        isLoading$,
        error$
    ).pipe(map(({ data }) => data?.measuringPointComment));
}

export function fetchComments$(
    measuringPointId: string,
    withReplies: boolean,
    isLoading$: BehaviorSubject<boolean>,
    error$: BehaviorSubject<Error | undefined>
) {
    return fetch$<MeasuringPointCommentsQuery, MeasuringPointCommentsQueryVariables>(
        MEASURING_POINT_COMMENTS_QUERY,
        { measuringPointId, withReplies },
        isLoading$,
        error$
    ).pipe(map(({ data }) => data?.measuringPointComments as MeasuringPointCommentType[]));
}

export function formatNoSeconds(date: Date, timezone: string): string {
    return format(date, { timezone, hideSeconds: true });
}
